import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import useEvents from "@app/_hooks/useEvents";
import type { Cart, CartItem } from "@server/gql/graphql";
import { removeFromCart } from "@server/requests/checkout/removeFromCart";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

type RemoveCartItem = <T extends boolean = false>(
  input: CartItem,
  async?: T,
) => T extends true ? Promise<Cart | undefined> : void;

export default function useRemoveCartItem() {
  const { removeFromCart: track } = useEvents();
  const { serviceLocale: locale } = useMarket().state.market;
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isError, isPending, isSuccess } = useMutation({
    mutationFn: async (incomingItem: CartItem) => {
      const cartId = getCartCookie()?.value;
      if (!cartId) return Promise.resolve(undefined);
      return removeFromCart({
        partNo: incomingItem.partNo,
        locale,
        cartId,
      });
    },
    onMutate: async (incomingItem) => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["checkout"] });
      await queryClient.cancelQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      const previousCart = queryClient.getQueryData<Cart>(["cart"]);

      queryClient.setQueryData<Cart>(["cart"], (currentCart) => {
        if (!currentCart) {
          return;
        }

        const updatedItems = currentCart.items.filter(
          (item) => item.partNo !== incomingItem.partNo,
        );

        const newTotal = updatedItems.reduce(
          (sum, item) => sum + item.totalGrossAmount,
          0,
        );

        return {
          ...currentCart,
          items: updatedItems,
          totalGrossAmount: newTotal + currentCart.totalFreightInclVat,
        };
      });

      return { previousCart };
    },
    onSuccess: (data, incomingItem) => {
      if (data) {
        track(incomingItem, data);
      }
    },
    onError: (
      error,
      input,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["checkout"] });
      void queryClient.invalidateQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });

  const removeCartItem = useCallback(
    (input: CartItem, async = false) => {
      if (async) return mutateAsync(input);
      mutate(input);
    },
    [mutate, mutateAsync],
  );

  return {
    removeCartItem: removeCartItem as RemoveCartItem,
    isErrorRemoveCartItem: isError,
    isPendingRemoveCartItem: isPending,
    isSuccessRemoveCartItem: isSuccess,
  };
}
